/**
 * Phase 11 / Task 11.1 (DEC-120) — InReview flag codes + shared DTOs.
 *
 * Pure, dependency-free contract shared by the API flag engine, the queue
 * endpoints, and the web-dispatch queue UI. The flag codes here are the exact
 * strings stored in `jobs.inreview_flags` (jsonb array).
 */

/** The six flag codes computed by Task 11.1's engine. */
export const INREVIEW_FLAG_CODES = [
  'GPS_OUTSIDE_RANGE',
  'DISPATCHER_COMPLETED',
  'VOLUME_ANOMALY',
  'MISSING_GAUGE',
  'MISSING_SIGNATURE',
  'DUPLICATE_TIMESTAMPS',
  // Phase 11 / Task 11.4 (DEC-125) — set when a truck pusher edits the reviewed
  // net BBL. Unlike the six above (computed at completion by Task 11.1's engine),
  // this flag is attached post-hoc by the volume-edit endpoint. Informational —
  // NOT ack-required (see ACK_REQUIRED_FLAG_CODES; consistent with DEC-124).
  'BILLING_FIELD_EDITED',
  // Phase 11 / Task 11.5 (DEC-126) — set when a truck pusher escalates a ticket
  // for TENANT_ADMIN review. Attached post-hoc by the escalate endpoint; the
  // ticket STAYS in IN_REVIEW (escalate does not advance to billing).
  // Informational — NOT ack-required.
  'ESCALATED',
  // OPEN-039 — the ticket has billable substance (volume or hours) but no rate
  // resolved anywhere in the chain (customer → operator → job-type default), so
  // it CANNOT produce billable events. Attached by applyCompletionReview, which
  // then routes the ticket INTO review instead of skipping it: DEC-190 already
  // refuses to let a truck pusher approve such a ticket silently, and a ticket
  // that never stops at review would otherwise slip past that rule entirely by
  // taking the clean path. Informational — NOT ack-required, because the approve
  // route already blocks on the same condition and offers the audited override;
  // an acknowledgment here would be a second gate that means nothing on its own.
  'MISSING_RATE',
  // DEC-319 (d) — a PICKUP arrival whose GPS could not be checked: the driver had a fix
  // but the pickup site had no location to measure it against (verification_result
  // UNVERIFIABLE). Computed at completion like GPS_OUTSIDE_RANGE, but behind its own
  // setting, flagGpsUnverifiable, which defaults OFF (migration 0126) — a tenant turns
  // it on once its batteries are placed. Informational — NOT ack-required.
  'GPS_UNVERIFIABLE',
  // DEC-321 / OPEN-082 — a dispatcher sent a stuck job with a captured load into review
  // ("force to review"). The load never reached disposal and the driver never finished the
  // ticket, so the reviewer must see that before billing it: ACK-REQUIRED (Corbin,
  // 2026-10-12). An acknowledgement counts only when made after the job's latest
  // forced_review_at, so a ticket forced a second time needs a fresh one.
  'FORCED_BY_DISPATCH',
  // DEC-352 / OPEN-155 — a record's time could not be trusted as sent: the phone's clock was
  // more than 10 minutes off when it pushed (DEVICE_CLOCK), the record reached the server
  // later than the tenant's lateRecordThresholdHours (ARRIVED_LATE), or an older app that does
  // not report its clock sent a record that far out (CLOCK_UNKNOWN). The server used to REJECT
  // these (CLOCK_SKEW_TOO_LARGE), losing real offline work; it now accepts them and writes a
  // TIME_CHECK audit row, which computeInReviewFlags turns into this flag regardless of the
  // tenant's toggles. The review card says which case it was. Informational — NOT
  // ack-required (Corbin, 2026-10-15).
  'TIME_CHECK',
  // DEC-357 / OPEN-144 — a second, different run ticket for this job is parked in
  // sync_conflicts (DUPLICATE_CAPTURE, DEC-350). Computed by the flag engine whenever such a
  // row is OPEN, so a ticket carrying one never skips review. NOT ack-required: an
  // acknowledgement does not clear it. Only a decision does (keep the saved ticket, or use
  // the parked one), and the approve route refuses on the OPEN row itself
  // (PARKED_CAPTURE_UNRESOLVED), not on this flag.
  'PARKED_CAPTURE',
  // #52 / DEC-358 #5 — the job type charges a customer disposal fee and the water went to a
  // site, but no fee is set for this customer there on the job's day. Attached by
  // applyCompletionReview (so a clean ticket stops at review instead of skipping it with the
  // fee silently missing) and shown live by the detail endpoint, like MISSING_RATE. The
  // approve route blocks on the same condition and offers "Approve without disposal fee"
  // with a reason, so — as for MISSING_RATE — it is informational, NOT ack-required.
  'MISSING_DISPOSAL_FEE',
  // #60 / DEC-365 (D8) — the job's pickup site was changed (JOB_SITE_CHANGED), by the driver
  // before Complete or by the office. Computed by the flag engine from the audit row, so a
  // ticket whose site changed before Complete stops at review (like TIME_CHECK); attached
  // directly when the office changes the site of a ticket already in review. Informational —
  // NOT ack-required; the timeline says what changed and why.
  'SITE_CHANGED',
  // Bug 2 (Corbin, 2026-10-17) — the job type ends in a disposal and a load was hauled, but no
  // ACTUAL disposal was recorded (the driver never arrived at one, or the office completed the
  // job). The price comes only from the actual disposal, so approve refuses until the office
  // records it (POST /v1/inreview/:id/actual-disposal, audited); the intended (planned) place
  // is shown as a hint, never billed. Informational — NOT ack-required: only recording the
  // actual disposal clears it.
  'DISPOSAL_NOT_RECORDED',
  // OPEN-121 — the job type REQUIRES a load photo, and a capture of the job either reported no
  // photo (photos_claimed 0), skipped it with a reason (photo_skip_reason), or did not report at
  // all (photos_claimed NULL: an older app build, or a non-driver writer such as the office).
  // Computed at completion from the capture payload, never from photo_s3_keys (photos upload
  // later on a slow queue). Behind flagMissingPhoto (default ON; it only fires on REQUIRED job
  // types). Informational — NOT ack-required, like MISSING_SIGNATURE.
  'MISSING_PHOTO',
  // OPEN-170 — LIVE only, never stored in inreview_flags: a REQUIRED-photo job type whose capture
  // claimed photos (photos_claimed > 0), none of which reached the server (photo_s3_keys empty),
  // more than PHOTO_ARRIVAL_GRACE_HOURS (24) after the capture synced (server time): an upload
  // aborted on the phone. Behind flagMissingPhoto. Derived at read time by the In Review list
  // and detail, like DISPOSAL_NOT_RECORDED. Informational — NOT ack-required, never blocks approve.
  'PHOTO_NOT_RECEIVED',
  // Firestone gap 10 — the ticket has captures and none of them recorded a truck
  // (haul_captures.truck_id empty on every one), so it exports with no QuickBooks Class (the
  // export takes the Class from the first capture that has a truck) and the office cannot see
  // which truck ran it. The phone requires a truck to start a shift and a shift to arrive, so
  // this is a backstop: an older build, a non-driver writer, or a capture with no shift on
  // record. Computed at completion, so it routes a clean ticket into review. Behind
  // flagMissingTruck (migration 0254, default ON). Informational — NOT ack-required, never
  // blocks approve. A placeholder truck (e.g. a "999" unit) is NOT detected: that needs a
  // definition from Corbin (ledger).
  'MISSING_TRUCK',
  // OPEN-217 / DEC-382 — a driver entry broke one of the hauler's rules and the phone did not
  // settle it: a BLOCK still failing, a WARNING not confirmed for that value, or a ticket from a
  // build without rules (or under a rule added since). Computed at completion by the same shared
  // evaluator the phone runs (api services/validation-rules-check.ts). Behind flagValidationRule
  // (migration 0266, default ON). Informational — NOT ack-required, never blocks approve. A
  // warning the driver confirmed is shown on the ticket instead, never flagged.
  'VALIDATION_RULE',
] as const;
export type InReviewFlagCode = (typeof INREVIEW_FLAG_CODES)[number];

/**
 * Phase 11 / Task 11.3 (DEC-124) — flags a truck pusher MUST explicitly
 * acknowledge before the ticket can be approved. Per the Task 11.3 prompt, only
 * GPS_OUTSIDE_RANGE gates approval; the other flags are informational. Kept as a
 * data-driven set so the acknowledgment table/endpoint stay generic and a future
 * flag can be made ack-required without a schema change.
 */
export const ACK_REQUIRED_FLAG_CODES = ['GPS_OUTSIDE_RANGE', 'FORCED_BY_DISPATCH'] as const;
export type AckRequiredFlagCode = (typeof ACK_REQUIRED_FLAG_CODES)[number];

export function isAckRequiredFlag(code: string): code is AckRequiredFlagCode {
  return (ACK_REQUIRED_FLAG_CODES as readonly string[]).includes(code);
}

/** One acknowledgment row (GET /v1/inreview/:jobId/acknowledgments). */
export interface InReviewFlagAcknowledgment {
  flagCode: InReviewFlagCode;
  acknowledgedBy: string | null;
  acknowledgedAt: string;
  notes: string | null;
}

/** The hauler-configurable InReview settings fields (API DTO shape). */
export interface InReviewSettings {
  requireInreviewForAllTickets: boolean;
  flagGpsOutsideRange: boolean;
  flagGpsDisposalJobTypesOnly: boolean;
  /** DEC-319 (d) — flag pickups whose GPS could not be checked. Default OFF. */
  flagGpsUnverifiable: boolean;
  flagDispatcherCompleted: boolean;
  flagVolumeAnomaly: boolean;
  volumeAnomalyThresholdBbl: number;
  flagMissingGauge: boolean;
  flagMissingSignature: boolean;
  /** OPEN-121 — flag a REQUIRED-photo job whose capture has no photo or a skip reason. Default ON. */
  flagMissingPhoto: boolean;
  /** Firestone gap 10 — flag a ticket whose captures recorded no truck (no QuickBooks Class). Default ON. */
  flagMissingTruck: boolean;
  /** OPEN-217 / DEC-382 — flag a ticket whose driver entries broke a hauler rule. Default ON. */
  flagValidationRule: boolean;
  flagDuplicateTimestamps: boolean;
  escalationThresholdHours: number;
  /**
   * DEC-352 — hours after which a record reaching the server counts as arriving late
   * (TIME_CHECK, ARRIVED_LATE). Default 24, the window that used to reject the record.
   */
  lateRecordThresholdHours: number;
  /**
   * #60 / DEC-365 (D8) — "Send tickets to review when the driver changed the site". ON: the
   * SITE_CHANGED flag routes a clean ticket into review. OFF: it only rides along. Default ON.
   */
  routeSiteChangeToReview: boolean;
  /**
   * OPEN-169 addendum — Send Back to the driver is allowed for this hauler. Default OFF, and
   * it cannot be turned on while DRIVER_CORRECTION_FLOW_AVAILABLE is false.
   */
  allowSendBack: boolean;
}

/**
 * OPEN-169 addendum — whether the phone app has a Send Back correction flow. Today a sent-back
 * ticket opens a read-only screen on the phone, so it would sit at COMPLETED unbilled. While
 * this is false the in-review settings PATCH refuses allowSendBack: true
 * (SEND_BACK_NEEDS_PHONE_UPDATE). Flip it to true when the phone's Send Back correction flow
 * ships.
 */
export const DRIVER_CORRECTION_FLOW_AVAILABLE = false;

/** Documented defaults — mirror the migration 0048 (and 0126, 0176, 0189, 0242, 0246, 0254, 0266) column defaults exactly. */
export const INREVIEW_SETTINGS_DEFAULTS: InReviewSettings = {
  requireInreviewForAllTickets: false,
  flagGpsOutsideRange: true,
  flagGpsDisposalJobTypesOnly: true,
  flagGpsUnverifiable: false,
  flagDispatcherCompleted: true,
  flagVolumeAnomaly: true,
  volumeAnomalyThresholdBbl: 200,
  flagMissingGauge: true,
  flagMissingSignature: false,
  flagMissingPhoto: true,
  flagMissingTruck: true,
  flagValidationRule: true,
  flagDuplicateTimestamps: false,
  escalationThresholdHours: 48,
  lateRecordThresholdHours: 24,
  routeSiteChangeToReview: true,
  allowSendBack: false,
};

/** One row in the GET /v1/inreview queue response. */
export interface InReviewQueueRow {
  id: string;
  haulId: string | null;
  status: string;
  siteName: string | null;
  operatorName: string | null;
  driverName: string | null;
  dispatcherName: string | null;
  netBbl: number;
  capturedAt: string | null;
  inreviewAt: string | null;
  inreviewFlags: InReviewFlagCode[];
  flagCount: number;
  isStale: boolean;
}
